import { FireBook, FireInsight } from '../models/FirestoreModels';
import {
  firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as limitQuery,
} from '../config/firebase';

// Type alias for Firestore document
type FirestoreDoc = any;

/**
 * Repository layer for book data access
 * Handles all Firestore operations for books and insights
 */
export class BookRepository {
  private static readonly BOOKS_COLLECTION = 'books_book';
  private static readonly INSIGHTS_COLLECTION = 'books_insight';

  /**
   * Fetch a book by ID and language
   */
  static async getBookById(bookId: string, languageCode: string = 'en'): Promise<FireBook | null> {
    try {
      const docId = `${bookId}_${languageCode}`;
      const bookDocRef = doc(firestore, this.BOOKS_COLLECTION, docId);
      const bookDoc = await getDoc(bookDocRef);

      if (!bookDoc.exists()) {
        console.log(`Book not found: ${docId}`);
        return null;
      }

      return {
        ...bookDoc.data(),
        id: bookId  // Use the clean bookId passed in, not the full doc.id
      } as FireBook;
    } catch (error) {
      console.error('Error fetching book:', error);
      throw error;
    }
  }

  /**
   * Fetch multiple books by IDs in parallel (batch operation)
   * More efficient than calling getBookById multiple times
   */
  static async getBooksById(
    bookIds: string[],
    languageCode: string = 'en'
  ): Promise<FireBook[]> {
    try {
      if (bookIds.length === 0) {
        return [];
      }

      console.log(`📦 Repository batch fetching ${bookIds.length} books`);
      const startTime = Date.now();

      // Fetch all books in parallel
      const bookPromises = bookIds.map(bookId => {
        const docId = `${bookId}_${languageCode}`;
        const bookDocRef = doc(firestore, this.BOOKS_COLLECTION, docId);
        return getDoc(bookDocRef);
      });

      const bookDocs = await Promise.all(bookPromises);
      const fetchTime = Date.now() - startTime;
      
      // Filter out non-existent documents and map to FireBook
      const books = bookDocs
        .filter((doc) => doc.exists())
        .map((doc) => {
          const data = doc.data();
          const cleanId = doc.id.split('_')[0];  // Extract clean ID: "53_en" → "53"
          return {
            ...data,
            id: cleanId
          } as FireBook;
        });

      if (fetchTime > 1000) {
        console.log(`⏱️ Firestore fetch took ${fetchTime}ms for ${bookIds.length} books (${books.length} found)`);
      }
      
      return books;
    } catch (error) {
      console.error('Error batch fetching books:', error);
      throw error;
    }
  }

  /**
   * Fetch insights for a book
   */
  static async getInsightsByIds(
    insightIds: string[], 
    languageCode: string = 'en'
  ): Promise<FireInsight[]> {
    try {
      if (!insightIds || insightIds.length === 0) {
        return [];
      }

      // Fetch all insights in parallel
      const insightPromises = insightIds.map(id => {
        const docId = `${id}_${languageCode}`;
        const insightDocRef = doc(firestore, this.INSIGHTS_COLLECTION, docId);
        return getDoc(insightDocRef);
      });

      const insightDocs = await Promise.all(insightPromises);
      
      // Filter out non-existent documents and map to FireInsight
      return insightDocs
        .filter((doc) => doc.exists())
        .map((doc) => ({
          ...doc.data(),
          id: doc.id.split('_')[0]  // Clean ID
        } as FireInsight));
    } catch (error) {
      console.error('Error fetching insights:', error);
      throw error;
    }
  }

  /**
   * Fetch insights from multiple books
   * Useful for insights feed where we want to sample insights across many books
   */
  static async getInsightsByBookIds(
    bookIds: string[],
    languageCode: string = 'en',
    limit?: number
  ): Promise<FireInsight[]> {
    try {
      if (!bookIds || bookIds.length === 0) {
        return [];
      }

      // Fetch all books to get their insight IDs
      const bookPromises = bookIds.map(bookId => this.getBookById(bookId, languageCode));
      const books = (await Promise.all(bookPromises)).filter(book => book !== null) as FireBook[];

      // Collect all insight IDs from all books
      const allInsightIds: string[] = [];
      books.forEach(book => {
        if (book.insightIds && book.insightIds.length > 0) {
          allInsightIds.push(...book.insightIds);
        }
      });

      // If limit specified, shuffle and slice
      let insightIdsToFetch = allInsightIds;
      if (limit && allInsightIds.length > limit) {
        // Shuffle array
        const shuffled = [...allInsightIds].sort(() => Math.random() - 0.5);
        insightIdsToFetch = shuffled.slice(0, limit);
      }

      // Fetch all insights
      return await this.getInsightsByIds(insightIdsToFetch, languageCode);
    } catch (error) {
      console.error('Error fetching insights by book IDs:', error);
      throw error;
    }
  }

  /**
   * Fetch a single insight
   */
  static async getInsightById(
    insightId: string, 
    languageCode: string = 'en'
  ): Promise<FireInsight | null> {
    try {
      const docId = `${insightId}_${languageCode}`;
      const insightDocRef = doc(firestore, this.INSIGHTS_COLLECTION, docId);
      const insightDoc = await getDoc(insightDocRef);

      if (!insightDoc.exists()) {
        console.log(`Insight not found: ${docId}`);
        return null;
      }

      return {
        ...insightDoc.data(),
        id: insightDoc.id
      } as FireInsight;
    } catch (error) {
      console.error('Error fetching insight:', error);
      throw error;
    }
  }


  /**
   * Fetch featured books
   */
  static async getFeaturedBooks(
    languageCode: string = 'en',
    limit: number = 10
  ): Promise<FireBook[]> {
    try {
      // Simplified query - just get books by language without ordering
      // This avoids needing a composite index
      const booksQuery = query(
        collection(firestore, this.BOOKS_COLLECTION),
        where('languageCode', '==', languageCode),
        limitQuery(limit * 2) // Get more to filter client-side
      );
      
      const snapshot = await getDocs(booksQuery);
      const allBooks = snapshot.docs.map((doc: any) => {
        const data = doc.data();
        // Use the id from data if it exists and is clean, otherwise extract from doc.id
        const cleanId = data.id || (doc.id.includes('_') ? doc.id.split('_')[0] : doc.id);
        return {
          ...data,
          id: cleanId
        } as FireBook;
      });
      
      // Client-side filtering and sorting
      const featuredBooks = allBooks
        .filter((book: FireBook) => book.isFeatured === true)
        .sort((a: FireBook, b: FireBook) => {
          // Sort by publishedAt if available
          if (a.publishedAt && b.publishedAt) {
            return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
          }
          return 0;
        })
        .slice(0, limit);
      
      if (featuredBooks.length > 0) {
        return featuredBooks;
      }
      
      // If no featured books, return all books (already sorted)
      return allBooks
        .sort((a: FireBook, b: FireBook) => {
          if (a.publishedAt && b.publishedAt) {
            return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
          }
          return 0;
        })
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching featured books:', error);
      
      // If even the simple query fails, try without any conditions
      try {
        console.log('Trying simplest query...');
        const simpleQuery = query(
          collection(firestore, this.BOOKS_COLLECTION),
          limitQuery(limit)
        );
        
                 const simpleSnapshot = await getDocs(simpleQuery);
         return simpleSnapshot.docs
           .map((doc: any) => ({
             ...doc.data(),
             id: doc.id.split('_')[0]  // Clean ID
           } as FireBook))
           .filter((book: any) => !languageCode || book.languageCode === languageCode);
      } catch (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
        throw error;
      }
    }
  }

  /**
   * Fetch all books for a language (no limit)
   */
  static async getAllBooks(
    languageCode: string = 'en'
  ): Promise<FireBook[]> {
    try {
      const booksQuery = query(
        collection(firestore, this.BOOKS_COLLECTION),
        where('languageCode', '==', languageCode)
      );
      
      const snapshot = await getDocs(booksQuery);
      return snapshot.docs.map((doc: any) => {
        const data = doc.data();
        // Use the id from data if it exists and is clean, otherwise extract from doc.id
        const cleanId = data.id || (doc.id.includes('_') ? doc.id.split('_')[0] : doc.id);
        return {
          ...data,
          id: cleanId
        } as FireBook;
      });
    } catch (error) {
      console.error('Error fetching all books:', error);
      return [];
    }
  }

  /**
   * Fetch popular books (by users practicing)
   */
  static async getPopularBooks(
    languageCode: string = 'en',
    limit: number = 10
  ): Promise<FireBook[]> {
    try {
      // Fetch more books than needed and sort client-side to avoid composite index
      const popularQuery = query(
        collection(firestore, this.BOOKS_COLLECTION),
        where('languageCode', '==', languageCode),
        limitQuery(limit * 3) // Get 3x to have enough for sorting
      );
      
      const snapshot = await getDocs(popularQuery);
      const books = snapshot.docs.map((doc: any) => {
        const data = doc.data();
        // Use the id from data if it exists and is clean, otherwise extract from doc.id
        const cleanId = data.id || (doc.id.includes('_') ? doc.id.split('_')[0] : doc.id);
        return {
          ...data,
          id: cleanId
        } as FireBook;
      });
      
      // Client-side sort by popularity
      return books
        .sort((a: FireBook, b: FireBook) => {
          const aPopularity = a.statistics?.usersPractising || 0;
          const bPopularity = b.statistics?.usersPractising || 0;
          return bPopularity - aPopularity;
        })
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching popular books:', error);
      throw error;
    }
  }

  /**
   * Search books by title or author
   */
  static async searchBooks(
    searchQuery: string,
    languageCode: string = 'en',
    limit: number = 20
  ): Promise<FireBook[]> {
    try {
      // Note: This is a simple implementation. For better search,
      // consider using Algolia or Firebase Extensions
      const lowerQuery = searchQuery.toLowerCase();
      
      const booksQuery = query(
        collection(firestore, this.BOOKS_COLLECTION),
        where('languageCode', '==', languageCode),
        limitQuery(limit * 2) // Get more to filter client-side
      );
      
      const snapshot = await getDocs(booksQuery);

      // Client-side filtering for title and author
      return snapshot.docs
        .map((doc: any) => ({
          ...doc.data(),
          id: doc.id.split('_')[0]  // Clean ID
        } as FireBook))
        .filter((book: any) => 
          book.title.toLowerCase().includes(lowerQuery) ||
          book.author.toLowerCase().includes(lowerQuery) ||
          book.localizedTitle?.toLowerCase().includes(lowerQuery)
        )
        .slice(0, limit);
    } catch (error) {
      console.error('Error searching books:', error);
      throw error;
    }
  }
} 